'use client';

import { useCallback, useEffect, useRef, useState } from 'react';
import type { ChangeEvent } from 'react';
import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';

dayjs.extend(utc);

export type SkipDirection = 'forward' | 'back';

interface TimeState {
    currentTime: number;
    timeRemaining: number;
}

export interface UseAudioPlayerArgs {
    source: string;
    isActive: boolean;
    onActivate: (isActive: boolean) => void;
    /** Whether the `sxm-media-play` event bubbles. Preserves each caller's original analytics behavior. */
    playEventBubbles?: boolean;
}

export const formatAudioTime = (time: number): string => dayjs.utc(time * 1000).format('m:ss');

export const computeProgressPercent = (currentTime: number, duration: number): number =>
    duration > 0 ? Math.floor((currentTime / duration) * 10000) / 100 : 0;

export const computeTimeRemaining = (duration: number, currentTime: number): number =>
    Math.max((duration || 0) - currentTime, 0);

export const skipAudio = (audioElement: HTMLAudioElement | null, direction: SkipDirection): void => {
    if (direction === 'forward') {
        if (audioElement) audioElement.currentTime += 15;
        audioElement?.dispatchEvent(new CustomEvent('sxm-media-forward'));
    }
    if (direction === 'back') {
        if (audioElement) audioElement.currentTime -= 15;
        audioElement?.dispatchEvent(new CustomEvent('sxm-media-backward'));
    }
};

export function useAudioPlayer({ source, isActive, onActivate, playEventBubbles = false }: UseAudioPlayerArgs) {
    const audioRef = useRef<HTMLAudioElement>(null);
    const hasStartedPlaybackRef = useRef(false);

    const [isPlayingInternal, setIsPlaying] = useState<boolean>(false);
    const [duration, setDuration] = useState(0);
    const [progressPercent, setProgressPercent] = useState(0);
    const [time, setTime] = useState<TimeState>({ currentTime: 0, timeRemaining: 0 });

    // Reset playback state when the source changes. Adjusting state during render (rather than in an
    // effect) avoids an extra commit with stale state — see https://react.dev/learn/you-might-not-need-an-effect.
    const [prevSource, setPrevSource] = useState(source);
    if (prevSource !== source) {
        setPrevSource(source);
        setIsPlaying(false);
        setDuration(0);
        setProgressPercent(0);
        setTime({ currentTime: 0, timeRemaining: 0 });
    }

    const isPlaying = isActive ? isPlayingInternal : false;
    const isEnded = !isPlaying && duration > 0 && time.currentTime >= duration;
    const showPauseButton = !isEnded && isPlaying;
    const showPlayButton = !isEnded && !isPlaying;

    const handleAudioEnded = () => {
        setIsPlaying(false);
        audioRef.current?.dispatchEvent(new CustomEvent('sxm-media-complete'));
    };

    const handleScrub = (e: ChangeEvent<HTMLInputElement>) => {
        const audioElement = audioRef.current;
        const nextTime = Number(e.target.value);

        setTime({ currentTime: nextTime, timeRemaining: computeTimeRemaining(duration, nextTime) });
        setProgressPercent(computeProgressPercent(nextTime, duration));

        if (audioElement) {
            audioElement.currentTime = nextTime;
        }
    };

    const handleLoadedMetadata = useCallback(() => {
        const nextDuration = audioRef.current?.duration || 0;

        setDuration(nextDuration);
        setTime((prev) => ({ ...prev, timeRemaining: computeTimeRemaining(nextDuration, prev.currentTime) }));
    }, []);

    const setAudioElementRef = useCallback(
        (node: HTMLAudioElement | null) => {
            audioRef.current = node;

            if (node?.readyState && node.readyState >= 1) {
                handleLoadedMetadata();
            }
        },
        [handleLoadedMetadata],
    );

    const handleTimeUpdate = () => {
        const currentTime = audioRef.current?.currentTime ?? 0;

        setTime({ currentTime, timeRemaining: computeTimeRemaining(duration, currentTime) });
        setProgressPercent(computeProgressPercent(currentTime, duration || 0));
    };

    const handlePlayPause = () => {
        const newPlayingState = !isPlaying;

        onActivate(newPlayingState);
        setIsPlaying(newPlayingState);
    };

    const handleReplay = () => {
        const audioElement = audioRef.current;

        if (audioElement) {
            audioElement.currentTime = 0;
        }

        onActivate(true);
        setIsPlaying(true);
    };

    const skip = (direction: SkipDirection) => skipAudio(audioRef.current, direction);

    useEffect(() => {
        hasStartedPlaybackRef.current = false;
    }, [source]);

    useEffect(() => {
        const audioElement = audioRef.current;
        if (!audioElement) {
            return;
        }

        const handlePause = () => setIsPlaying(false);

        audioElement.addEventListener('pause', handlePause);
        return () => audioElement.removeEventListener('pause', handlePause);
    }, []);

    useEffect(() => {
        const audioElement = audioRef.current;

        if (isActive && isPlaying) {
            audioElement?.play();
        } else {
            audioElement?.pause();
        }
    }, [isPlaying, isActive]);

    useEffect(() => {
        const audioElement = audioRef.current;
        const isAtEnd = !!audioElement && audioElement.duration > 0 && audioElement.currentTime >= audioElement.duration;

        if (isPlaying) {
            hasStartedPlaybackRef.current = true;
            audioElement?.dispatchEvent(new CustomEvent('sxm-media-play', { bubbles: playEventBubbles }));
        } else if (hasStartedPlaybackRef.current && !isAtEnd) {
            audioElement?.dispatchEvent(new CustomEvent('sxm-media-pause'));
        }
    }, [isPlaying, playEventBubbles]);

    return {
        audioRef,
        setAudioElementRef,
        isPlaying,
        isEnded,
        duration,
        progressPercent,
        time,
        showPlayButton,
        showPauseButton,
        formatTime: formatAudioTime,
        handlePlayPause,
        handleReplay,
        handleScrub,
        handleAudioEnded,
        handleTimeUpdate,
        handleLoadedMetadata,
        skip,
    };
}
